<template>
  <div>
    <copy-features-dialog
      name="investment-area-search"
      :is-visible.sync="isCopyVisible"
      :is-loading.sync="isCopyLoading"
      input-layer-id="investment-area-search"
      :geojson="results"
      :geojson-copy-attributes="resultsAttributes"
      :append-layer-inputs="terrainGeojson ? assignCustomTerrainInputs : []"
      v-bind="copyWithOzeProjectBind"
    >
      <template #projectId.input="data">
        <v-col class="tw:relative">
          <data-input v-model="projectId" v-bind="data" left-col="6" />
          <base-button
            v-if="hasAddProjectButton"
            icon="mdi-plus"
            :tooltip="$i18n.t('sidebar.searchArea.addProject')"
            :disabled="isCopyLoading"
            class="tw:absolute tw:-right-11 tw:top-0.5"
            @click="toggleProjectAdding"
          />
        </v-col>
      </template>
      <template v-for="name in Object.keys(terrainInputValues)" #[`${name}.input`]="data">
        <v-col :cols="name === 'saveTerrain' ? 12 : 6" :key="name">
          <data-input v-model="terrainInputValues[data.name]" v-bind="data">
            <template v-if="name === 'saveTerrain'" #label>
              <span class="font--text">{{ data.label }}</span>
            </template>
          </data-input>
        </v-col>
      </template>
    </copy-features-dialog>
    <main-input-dialog
      ref="select-project-layer-dialog"
      v-if="projectLayerSelectionVisible"
      :is-visible.sync="projectLayerSelectionVisible"
      :title-translation="$i18n.t('dialog.title.selectLayer')"
      :inputs="projectLayerInput"
      agree-text="dialog.agreeChoose"
      disable-default-form-bottom-padding
      @agree="toggleProjectFeatureCard"
    >
      <template #[`projectLayer.input`]="data">
        <v-col cols="6">
          <data-input v-model="projectLayer" v-bind="data" />
        </v-col>
      </template>
    </main-input-dialog>
    <add-edit-feature-dialog
      v-if="projectFeatureCardVisible"
      :is-visible.sync="projectFeatureCardVisible"
      v-bind="projectFeatureCard"
      @done="onProjectAdded"
      :feature-metadata="{ properties: {} }"
    />
  </div>
</template>

<script>
import { get, call } from 'vuex-pathify';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

import MainInputDialog from '@/components/MainInputDialog';
import AddEditFeatureDialog from '@/components/AddEditFeatureDialog';
import DataInput from '@/components/DataInput';

export default {
  name: 'OzeProjectCopyDialog',
  mixins: [prepareAttributesFeatureCardMixin],
  components: {
    AddEditFeatureDialog,
    MainInputDialog,
    DataInput,
    CopyFeaturesDialog: () => import('@/components/CopyFeaturesDialog'),
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    results: {
      type: Object,
      default: undefined,
    },
    resultsAttributes: {
      type: Array,
      default: () => [],
    },
    terrainGeojson: {
      type: Object,
    },
  },
  data: () => ({
    isCopyLoading: false,
    projectsLoading: false,
    projectLayerSelectionVisible: false,
    projectLayer: null,
    projectFeatureCardVisible: false,
    projectFeatureCard: null,
    projectId: null,

    terrainInputValues: {
      saveTerrain: false,
      terrainLayerId: null,
      terrainDescriptionAttribute: null,
      terrainDescriptionValue: null,
    },
  }),
  computed: {
    layers: get('layers/layers'),
    terrainDatasource: get('layers/metadata@:terrainDatasourceName'),
    ozeParcelsDatasource: get('admin/modulesMapping@oze.oze_parcels_datasource.datasource'),
    ozeProjectsDatasource: get('admin/modulesMapping@oze.oze_projects_datasource.datasource'),
    ozeProjectsDatasourceGeometryType: get('layers/metadata@:ozeProjectsDatasource.geometry_type'),
    ozeParcelsDatasourceIdName: get('layers/metadata@:ozeParcelsDatasource.attributes_schema.id_name'),
    ozeParcelsDatasourceGeometryName: get('layers/metadata@:ozeParcelsDatasource.attributes_schema.geometry_name'),
    isCopyVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    ozeParcelsLayers() {
      return Object.values(this.layers)
        .filter(layer => layer.data_source_name === this.ozeParcelsDatasource)
        .map(layer => ({
          value: layer.id,
          text: layer.name,
        }));
    },
    polygonLayers() {
      return Object.values(this.layers)
        .filter(layer => layer.geometry_type === 'polygon')
        .map(layer => ({
          value: layer.id,
          text: layer.name,
        }));
    },
    ozeProjectsLayers() {
      return Object.values(this.layers)
        .filter(layer => layer.data_source_name === this.ozeProjectsDatasource)
        .map(layer => ({
          value: layer.id,
          text: layer.name,
        }));
    },
    projectLayerInput() {
      return [
        {
          dataType: { name: 'select' },
          name: 'projectLayer',
          cols: '12',
          translationPath: 'default.layer',
          items: this.ozeProjectsLayers,
          rules: [v => !!v || 'rules.required'],
        },
      ];
    },
    copyWithOzeProjectBind() {
      return {
        dialogTitle: this.$i18n.t('sidebar.searchArea.addToOzeProject'),
        layerTitle: this.$i18n.t('sidebar.searchArea.copyFeaturesLayerTitle'),
        hideLayerSubtitle: true,
        mappingAttributesTitle: this.$i18n.t('sidebar.searchArea.mappingAttributesTitle'),
        agreeText: 'dialog.agreeAssign',
        customTargetLayers: this.ozeParcelsLayers,
        customCopyObjectsAction: this.copyWithOzeProject,
        formCols: this.hasAddProjectButton ? 11 : 12,
      };
    },
    hasAddProjectButton() {
      return this.ozeProjectsLayers?.length && !this.ozeProjectsDatasourceGeometryType;
    },
    copyToProjectBind() {
      return {
        name: 'projectId',
        dataType: { name: 'select' },
        items: this.projects,
        itemValue: 'id',
        itemText: 'desc',
        cols: 12,
        straightTitle: this.$i18n.t('sidebar.searchArea.project'),
        subtitle: this.$i18n.t('sidebar.searchArea.projectSubtitle'),
        loading: this.projectsLoading,
        disabled: this.isCopyLoading,
      };
    },
    /** Optional terrain save */
    computedTerrainLayerId() {
      return this.terrainInputValues.terrainLayerId;
    },
    terrainDatasourceName() {
      if (!this.computedTerrainLayerId) return;
      return this.layers[this.computedTerrainLayerId].data_source_name;
    },
    terrainLayerAttributes() {
      if (!this.computedTerrainLayerId) return [];
      return this.$_flattenStructure(
        this.getForm(
          this.layers[this.computedTerrainLayerId]?.form_schema.elements || [],
          this.terrainDatasource?.attributes_schema?.attributes || []
        )
      );
    },
    terrainTextAttributes() {
      if (!this.terrainLayerAttributes.length) return [];
      return this.terrainLayerAttributes?.filter(attribute => attribute.dataType.name === 'text') || [];
    },
  },
  watch: {
    'terrainInputValues.terrainLayerId': function () {
      this.terrainInputValues = {
        ...this.terrainInputValues,
        terrainDescriptionAttribute: null,
      };
    },
    isCopyVisible(nV) {
      if (!nV) this.projectId = null;
    },
    projectLayer(nV) {
      if (nV) localStorage.setItem('oze_project_layer', nV);
    },
  },
  methods: {
    addMultiRelations: call('relations/addMultiRelations'),
    editMultipleDataSourceFeatures: call('layers/editMultipleDataSourceFeatures'),
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    addDatasourceFeature: call('layers/addDatasourceFeature'),
    toggleProjectAdding() {
      if (this.ozeProjectsLayers?.length === 1) {
        this.projectLayer = this.ozeProjectsLayers[0].value;
        this.toggleProjectFeatureCard();
      } else {
        const projectLayer = +localStorage.getItem('oze_project_layer');
        if (projectLayer && this.ozeProjectsLayers.some(layer => layer.value === projectLayer))
          this.projectLayer = projectLayer;
        else this.projectLayer = null;
        this.projectLayerSelectionVisible = true;
      }
    },
    toggleProjectFeatureCard(projectLayer = this.projectLayer) {
      this.projectLayerSelectionVisible = false;
      this.projectFeatureCard = {
        title: this.$i18n.t('dialog.title.addNewObjectName', { layer_name: this.layers[projectLayer].name }),
        featureData: {
          datasource: this.ozeProjectsDatasource,
          layer: projectLayer,
        },
      };
      this.projectFeatureCardVisible = this.projectFeatureCardVisible = true;
    },
    async onProjectAdded(closeDialog, metadata, newItemId) {
      await this.getProjects(newItemId);
      closeDialog();
    },
    async getProjects(setProjectId = null) {
      this.projectsLoading = true;
      try {
        const features = await this.getDataSourceFeatures({
          dataSource: this.ozeProjectsDatasource,
          idsDescsOnly: true,
        });
        this.projects = features;
        if (setProjectId || setProjectId === 0) this.projectId = setProjectId;
      } finally {
        this.projectsLoading = false;
      }
    },
    assignCustomTerrainInputs({ isLoading }) {
      return [
        this.copyToProjectBind,
        {
          name: 'saveTerrain',
          label: this.$i18n.t('sidebar.searchArea.saveTerrain'),
          dataType: { name: 'checkbox' },
          leftCol: 0,
          readonly: isLoading,
        },
        ...(this.terrainInputValues.saveTerrain
          ? [
              {
                name: 'terrainLayerId',
                translationPath: 'sidebar.searchArea.terrainLayer',
                dataType: { name: 'select' },
                items: this.polygonLayers,
                cols: '12',
                rules: [v => !!v || v == '0' || 'rules.required'],
                noDataText: this.$i18n.t('sidebar.copyObjectsGeometry.noLayersMatchingGeometryInProject'),
                readonly: isLoading,
              },
              {
                name: 'terrainDescriptionAttribute',
                translationPath: 'sidebar.searchArea.descriptionAttribute',
                dataType: { name: 'select' },
                itemText: 'label',
                itemValue: 'attribute',
                items: this.terrainTextAttributes,
                cols: '12',
                clearable: true,
                disabled: !this.computedTerrainLayerId,
                readonly: isLoading,
              },
              {
                name: 'terrainDescriptionValue',
                translationPath: 'sidebar.searchArea.descriptionValue',
                dataType: { name: 'text' },
                cols: '12',
                disabled: !this.computedTerrainLayerId || !this.terrainInputValues.terrainDescriptionAttribute,
                readonly: isLoading,
              },
            ]
          : []),
      ];
    },
    async copyWithOzeProject(geojson, layerId) {
      const payload = {
        data_source_name: this.ozeParcelsDatasource,
        insert: geojson.features.map(feature => {
          return {
            ...feature.properties,
            [this.ozeParcelsDatasourceGeometryName]: { ...feature.geometry, crs: geojson.crs },
          };
        }),
        params: { layer_id: layerId },
      };

      /** Add parcels to the system */
      const r = await this.editMultipleDataSourceFeatures(payload);
      const newFeaturesIds = r.data.data.insert?.map(item => item[this.ozeParcelsDatasourceIdName]);
      let relationParcelsFeatures = newFeaturesIds.map(featureId => {
        return {
          from_datasource_name: this.ozeParcelsDatasource,
          from_feature_fid: featureId,
          to_datasource_name: this.ozeProjectsDatasource,
          to_feature_fid: this.projectId,
        };
      });

      /** Save terrain if geometry is provided and terrain saving is enabled via checkbox */
      if (this.terrainGeojson && this.terrainInputValues.saveTerrain) {
        const geomAttributeName = this.terrainDatasource.attributes_schema.geometry_name;
        const descAttr = this.terrainInputValues.terrainDescriptionAttribute;
        const descAttrValue = this.terrainInputValues.terrainDescriptionValue;

        /** Add terrain to the system */
        const newTerrainId = (
          await this.addDatasourceFeature({
            datasource: this.terrainDatasourceName,
            body: {
              [geomAttributeName]: this.terrainGeojson,
              ...(descAttr && descAttrValue && { [descAttr]: descAttrValue }),
            },
            params: { layer_id: this.terrainInputValues.terrainLayerId },
          })
        ).data.data;

        /** Push new relations from terrain to project nad parcels to relations stack */
        const parcelsToTerrainRelation =
          this.ozeParcelsDatasource !== this.terrainDatasourceName
            ? [...relationParcelsFeatures].map(feature => ({
                ...feature,
                to_datasource_name: this.terrainDatasourceName,
                to_feature_fid: newTerrainId,
              }))
            : [];
        relationParcelsFeatures = [
          ...relationParcelsFeatures,
          ...parcelsToTerrainRelation,
          {
            from_datasource_name: this.terrainDatasourceName,
            from_feature_fid: newTerrainId,
            to_datasource_name: this.ozeProjectsDatasource,
            to_feature_fid: this.projectId,
          },
        ];

        /** Refresh selected terrain layer on map */
        this.$root.$emit('refreshMvtSource', this.terrainInputValues.terrainLayerId);
      }

      /** Assign relations and refresh project relations if it is opened */
      if (!this.projectId) return;
      const currentlyOpenedDatasource = this.layers[this.$route.params.lid]?.data_source_name;
      await this.addMultiRelations({
        relations: relationParcelsFeatures,
        ...(+this.$route.params.fid === +this.projectId &&
          currentlyOpenedDatasource === this.ozeProjectsDatasource && {
            refresh: {
              feature_id: this.$route.params.fid,
              data_source_name: currentlyOpenedDatasource,
            },
          }),
      });
    },
  },
  mounted() {
    this.getProjects();
  },
};
</script>
